import { z } from 'zod'

/**
 * The model gateway, and what we can say about a model without the database.
 *
 * Every call goes through one vendor (OpenRouter) so that adding a model is a
 * row rather than a client, which is the whole reason a brand-citation report
 * can ask five vendors the same question. The provider in a slug is therefore
 * who *answered*, not who billed us — the ledger only ever says `openrouter`.
 */
export const AI_GATEWAY = 'openrouter'

/** `anthropic/claude-opus-5` -> `anthropic`. The gateway guarantees the shape;
 * anything without a slash is its own provider rather than an error. */
export function aiModelProvider(modelId: string): string {
  const slash = modelId.indexOf('/')
  return slash === -1 ? modelId : modelId.slice(0, slash)
}

/**
 * What a run is for, and the grain everything reading volume or spend groups
 * by. A dotted slug rather than a fixed list: the features that will use this
 * do not exist yet, and an enum here would be a migration for each one. Lower
 * case and dotted is enough to keep `report.summary` from also appearing as
 * `Report Summary` and reading as two things.
 */
export const aiPurposeSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9]*(\.[a-z0-9-]+)+$/,
    'Expected a dotted lower-case slug, e.g. report.summary',
  )

export type AiPurpose = z.infer<typeof aiPurposeSchema>

export interface AiTokenUsage {
  inputTokens: number
  outputTokens: number
  cacheReadTokens?: number
  cacheWriteTokens?: number
}

/**
 * Dollars per token, as the catalogue stores them — the gateway's own strings,
 * kept as strings until something needs the arithmetic.
 */
export interface AiTokenRates {
  inputUsdPerToken?: string | null
  outputUsdPerToken?: string | null
  cacheReadUsdPerToken?: string | null
  cacheWriteUsdPerToken?: string | null
}

function rate(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

/**
 * What a call cost according to the catalogue, in dollars.
 *
 * The fallback, not the answer: the gateway reports what it actually charged on
 * every response and that is what the ledger records, because the catalogue is
 * a mirror that can be a day stale and the invoice will not be. This exists for
 * the response that arrives without a cost on it.
 *
 * Null when the model is unpriced, rather than zero — zero is a claim the call
 * was free, and a topline quietly missing a model is worse than one that admits
 * it does not recognise it.
 */
export function aiTokenCost(rates: AiTokenRates, usage: AiTokenUsage): number | null {
  const input = rate(rates.inputUsdPerToken)
  const output = rate(rates.outputUsdPerToken)
  if (input === null || output === null) return null

  // Cache pricing is often absent even where caching happened. Charging those
  // tokens at the full input rate over-reports rather than flatters, which is
  // the direction an estimate should miss in.
  const cacheRead = rate(rates.cacheReadUsdPerToken) ?? input
  const cacheWrite = rate(rates.cacheWriteUsdPerToken) ?? input

  return (
    usage.inputTokens * input +
    usage.outputTokens * output +
    (usage.cacheReadTokens ?? 0) * cacheRead +
    (usage.cacheWriteTokens ?? 0) * cacheWrite
  )
}

/** Every token the call touched. What the ledger counts, because it is the one
 * number that is comparable across models; the split stays in `detail`. */
export function aiTotalTokens(usage: AiTokenUsage): number {
  return (
    usage.inputTokens +
    usage.outputTokens +
    (usage.cacheReadTokens ?? 0) +
    (usage.cacheWriteTokens ?? 0)
  )
}

/** Per-token prices are unreadable at the scale they are quoted, so anything
 * displaying one shows it per million. Display only — never stored. */
export function usdPerMillion(perToken: string | null | undefined): number | null {
  const parsed = rate(perToken)
  return parsed === null ? null : parsed * 1_000_000
}

/**
 * The gateway names a variant with a suffix on the slug — `:free`, `:nitro`,
 * `:batch` — and a variant is the same model on different terms rather than a
 * different model. `openai/gpt-6-luna:batch` is the same weights, queued,
 * cheaper and slower.
 *
 * Two consequences, both handled by stripping: the catalogue may or may not
 * list a variant as a row of its own, so the allowlist and the fallback rate
 * card have to fall back to the base slug rather than refusing a model the
 * deployment plainly enabled; and the batch tier answers on a timescale a
 * request-shaped job cannot hold open, so it is dispatched to its own queue.
 */
export const AI_MODEL_VARIANT = /:([a-z0-9-]+)$/

export function aiModelVariant(modelId: string): string | null {
  return AI_MODEL_VARIANT.exec(modelId)?.[1] ?? null
}

/** `openai/gpt-6-luna:batch` -> `openai/gpt-6-luna`. Idempotent on a slug
 * with no variant, so call sites do not have to test first. */
export function aiBaseModel(modelId: string): string {
  return modelId.replace(AI_MODEL_VARIANT, '')
}

/**
 * Whether a call on this slug is asynchronous in the sense that matters: it may
 * take hours rather than minutes, so it cannot share an expiry with the
 * interactive queue and nothing may block on it.
 */
export function isBatchModel(modelId: string): boolean {
  return aiModelVariant(modelId) === 'batch'
}
